import { useEffect, useState } from "react";
import { SiteSettings } from "@/lib/webContentTypes";
import {
  getAdminSettings,
  updateAdminSettings,
  webAdminChangePassword,
} from "@/lib/webContentApi";
import { defaultSiteSettings } from "@/lib/useSiteSettings";

interface Props {
  onAuthExpired: () => void;
}

export default function AdminSettings({ onAuthExpired }: Props) {
  const [config, setConfig] = useState<SiteSettings>({ ...defaultSiteSettings });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordSaved, setPasswordSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAdminSettings();
      setConfig({ ...defaultSiteSettings, ...data });
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : "No se pudo cargar configuracion";
      if (message.toLowerCase().includes("sesion") || message.toLowerCase().includes("token")) {
        onAuthExpired();
        return;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const updated = await updateAdminSettings(config);
      setConfig({ ...defaultSiteSettings, ...updated });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : "No se pudo guardar configuracion";
      if (message.toLowerCase().includes("sesion") || message.toLowerCase().includes("token")) {
        onAuthExpired();
        return;
      }
      setError(message);
    }
  };

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("La confirmacion de clave no coincide");
      return;
    }

    try {
      await webAdminChangePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 3000);
    } catch (passwordError) {
      const message = passwordError instanceof Error ? passwordError.message : "No se pudo cambiar la clave";
      if (message.toLowerCase().includes("sesion") || message.toLowerCase().includes("token")) {
        onAuthExpired();
        return;
      }
      setError(message);
    }
  };

  const fields: { key: keyof SiteSettings; label: string; type?: string; placeholder?: string }[] = [
    { key: "aprName", label: "Nombre corto del APR", placeholder: "APR Quechurehue" },
    { key: "aprFullName", label: "Nombre completo del APR", placeholder: "Comite de Agua Potable Rural..." },
    { key: "payUrl", label: "URL de pago de cuenta", type: "text", placeholder: "/pagar" },
    { key: "portalUrl", label: "URL del portal de usuarios", type: "url", placeholder: "https://..." },
    { key: "phone", label: "Telefono de contacto", placeholder: "+56 9 XXXX XXXX" },
    { key: "email", label: "Correo electronico", type: "email", placeholder: "contacto@aprquechurehue.cl" },
    { key: "address", label: "Direccion", placeholder: "Av. Principal 120" },
    { key: "schedule", label: "Horario de atencion", placeholder: "Lunes a Viernes: 09:00 - 14:00 hrs" },
    { key: "whatsapp", label: "Numero WhatsApp (sin +)", placeholder: "56912345678" },
  ];

  if (loading) {
    return <div className="text-sm text-slate-500">Cargando configuracion...</div>;
  }

  return (
    <div className="max-w-3xl space-y-10">
      <div>
        <h2 className="text-xl font-bold text-sky-900 mb-2">Configuracion del sitio</h2>
        <p className="text-slate-500 text-sm mb-8">
          Esta informacion se guarda en base de datos y se usa en la web publica.
        </p>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-5">
          {fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">{field.label}</label>
              <input
                type={field.type ?? "text"}
                value={config[field.key] ?? ""}
                onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                placeholder={field.placeholder}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400 transition-colors"
              />
            </div>
          ))}

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              className="flex items-center gap-2 px-8 py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm cursor-pointer transition-colors"
            >
              <i className="ri-save-line"></i>
              Guardar configuracion
            </button>
            {saved && (
              <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-semibold">
                <i className="ri-check-line"></i>Guardado correctamente
              </span>
            )}
          </div>
        </form>
      </div>

      <div className="border border-slate-200 rounded-2xl p-6 bg-white">
        <h3 className="text-lg font-bold text-sky-900 mb-1">Clave del panel admin web</h3>
        <p className="text-sm text-slate-500 mb-6">
          Esta clave es solo para administrar noticias, actividades y comunicados del sitio.
        </p>

        <form onSubmit={handlePasswordSave} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Clave actual</label>
            <input
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Nueva clave</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                minLength={8}
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Confirmar nueva clave</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                minLength={8}
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400"
              />
            </div>
          </div>

          <div className="flex items-center gap-4 pt-1">
            <button
              type="submit"
              className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm cursor-pointer transition-colors"
            >
              Actualizar clave
            </button>
            {passwordSaved && (
              <span className="text-emerald-600 text-sm font-semibold">
                Clave actualizada
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

